<template>
  <div
    class="app-container"
    :style="showTestEnvironmentBanner ? { '--test-env-banner-h': '2.25rem' } : undefined"
  >
    <div
      v-if="showTestEnvironmentBanner"
      class="test-env-banner"
      role="status"
      aria-live="polite"
    >
      {{ testEnvironmentBannerText }}
    </div>

    <!-- Mientras el router espera guards (check-status, módulos, permisos) -->
    <div
      v-if="routerNavLoading"
      class="router-nav-loading-strip"
      role="status"
      aria-live="polite"
    >
      <div
        class="spinner-border spinner-border-sm text-primary"
        role="presentation"
      ></div>
      <span class="router-nav-loading-strip-text">{{
        $t("routerNavLoading")
      }}</span>
    </div>

    <!-- Custom CSS creado en settings-->
    <custom-css />
    <CustomJavaScript />
    <router-view></router-view>

    <!-- Overlay tipo tutorial: ?focusON=id&message=... (para FAQ y cualquier página) -->
    <FocusTutorialOverlay />

    <!-- Componente de notificaciones flotantes global -->
    <FloatingNotification ref="globalFloatingNotification" />

    <!-- Migration Notification -->
    <MigrationNotification v-if="isAuthenticated" />

    <!-- Chat Widget (only if module is active and user is authenticated) -->
    <!-- Debug info: isChatModuleActive={{ isChatModuleActive }}, isAuthenticated={{ isAuthenticated }} -->
    <ChatWidget v-if="isChatModuleActive && isAuthenticated" />

    <!-- Temporary: Show chat widget if authenticated (for testing) - Remove this after debugging -->
    <!-- <ChatWidget v-if="isAuthenticated" /> -->
  </div>
</template>

<script>
import CustomCss from "@/components/CustomCss.vue";
import CustomJavaScript from "@/components/CustomJavaScript.vue";
import FloatingNotification from "@/components/gotrading/common/FloatingNotification.vue";
import MigrationNotification from "@/components/MigrationNotification.vue";
import ChatWidget from "@/modules/chat_module/components/ChatWidget.vue";
import FocusTutorialOverlay from "@/components/FocusTutorialOverlay.vue";
import { notificationManager } from "@/utils/notificationManager";
import { wencesApi, isLocalTestEnvironment } from "@/api/wencesApi";
import { isTenantTestEnvironment } from "@/utils/tenantTestEnvironment";
import { routerNavigationLoading } from "@/utils/routerNavigationLoading";
import {
  UI_ZOOM_DEFAULT,
  applyUiZoom,
  readCachedUiZoom,
  cacheUiZoom,
} from "@/utils/uiZoom";

export default {
  name: "App",
  components: {
    CustomCss,
    CustomJavaScript,
    FloatingNotification,
    MigrationNotification,
    ChatWidget,
    FocusTutorialOverlay,
  },
  data() {
    return {
      isChatModuleActive: false,
    };
  },
  computed: {
    routerNavLoading() {
      return routerNavigationLoading.show;
    },
    showTestEnvironmentBanner() {
      // Local (npm run serve) o tenant hospedado *-test (duntower-test, etc.)
      return isLocalTestEnvironment() || isTenantTestEnvironment();
    },
    testEnvironmentBannerText() {
      if (isTenantTestEnvironment() && !isLocalTestEnvironment()) {
        return this.$t("testEnvironmentBannerHosted");
      }
      return this.$t("testEnvironmentBanner");
    },
    isAuthenticated() {
      // Verificar si el usuario está autenticado
      // El sistema guarda 'token' y 'userId', no 'user' como objeto
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      return !!(token && userId);
    },
  },
  created() {
    // Cargar dark.css dinámicamente solo si NO estamos en rutas /templates/*
    this.loadDarkCssIfNeeded();
    // Zoom del usuario desde la cache local, antes de que responda el perfil
    this.applyUiZoomForRoute(this.$route);
    // Aplicar modo oscuro antes de que se renderice el contenido
    this.initDarkMode();
    // Verificar si el módulo de chat está activo (solo si hay token)
    console.log("App.vue created - isAuthenticated:", this.isAuthenticated);
    if (this.isAuthenticated) {
      console.log("App.vue: Checking chat module status...");
      this.checkChatModuleStatus();
    } else {
      console.log(
        "App.vue: User not authenticated, skipping chat module check"
      );
    }
  },
  mounted() {
    // Registrar la instancia del componente de notificaciones
    this.$nextTick(() => {
      if (this.$refs.globalFloatingNotification) {
        notificationManager.setInstance(this.$refs.globalFloatingNotification);
      } else {
        console.error("No se pudo inicializar NotificationManager");
      }
    });

    // Watch for authentication changes
    this.watchAuthentication();
  },
  watch: {
    $route(to, from) {
      // Cuando cambia la ruta, verificar si necesitamos cargar/remover dark.css
      this.handleDarkCssForRoute(to);
      this.applyUiZoomForRoute(to);
    },
    isAuthenticated(newVal, oldVal) {
      console.log("App.vue: Authentication changed:", { newVal, oldVal });
      // Si el usuario se autentica, verificar el módulo
      if (newVal) {
        console.log("App.vue: User authenticated, checking chat module...");
        this.checkChatModuleStatus();
      } else {
        // Si el usuario cierra sesión, ocultar el chat
        console.log("App.vue: User logged out, hiding chat");
        this.isChatModuleActive = false;
      }
    },
  },
  methods: {
    // Cargar dark.css dinámicamente solo si NO estamos en rutas /templates/*
    loadDarkCssIfNeeded() {
      const isTemplateRoute = this.$route?.path?.startsWith("/templates/");
      let darkCssLink = document.getElementById("dark-css-link");

      // modern-touch.css cuelga de este atributo: fuera de los templates PDF.
      // Atributo y no clase porque SideBar.vue pisa el class del <html>.
      if (isTemplateRoute) {
        document.documentElement.removeAttribute("data-ui");
      } else {
        document.documentElement.setAttribute("data-ui", "modern");
      }

      if (isTemplateRoute) {
        // Remover dark.css si estamos en una ruta de template
        if (darkCssLink) {
          darkCssLink.remove();
        }
      } else {
        // Cargar dark.css dinámicamente si no estamos en template y no está cargado
        if (!darkCssLink) {
          // Importar el CSS dinámicamente
          import("@/assets/css/dark.css")
            .then(() => {
              // El CSS se carga, pero necesitamos crear un link tag para poder removerlo después
              // Buscar el style tag que webpack crea para el CSS importado
              const styles = Array.from(document.styleSheets);
              let darkStyleSheet = null;

              for (let sheet of styles) {
                try {
                  if (sheet.href && sheet.href.includes("dark.css")) {
                    darkStyleSheet = sheet;
                    break;
                  }
                } catch (e) {
                  // Ignorar errores de CORS
                }
              }

              // Si encontramos el stylesheet, crear un link tag para poder removerlo
              if (darkStyleSheet && darkStyleSheet.href) {
                const link = document.createElement("link");
                link.id = "dark-css-link";
                link.rel = "stylesheet";
                link.href = darkStyleSheet.href;
                document.head.appendChild(link);
              }
            })
            .catch((err) => {
              console.warn("Error loading dark.css dynamically:", err);
            });
        }
      }
    },

    // Manejar dark.css según la ruta actual
    handleDarkCssForRoute(route) {
      this.loadDarkCssIfNeeded();
    },

    async checkChatModuleStatus() {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.log("Chat: No token, hiding widget");
          this.isChatModuleActive = false;
          return;
        }

        // Importar wencesApi dinámicamente
        const { wencesApi } = await import("@/api/wencesApi");
        const response = await wencesApi.get("/api/modules/active");

        console.log("Chat: Modules response:", response.data);

        let activeModules = [];
        if (response.data && Array.isArray(response.data)) {
          activeModules = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          activeModules = response.data.data;
        }

        console.log("Chat: Active modules:", activeModules);

        // Verificar si el módulo de chat está activo
        this.isChatModuleActive = activeModules.some(
          (module) => module.name === "chat_module"
        );

        console.log("Chat: Module active?", this.isChatModuleActive);
        console.log("Chat: User authenticated?", this.isAuthenticated);
      } catch (error) {
        console.error("Error checking chat module status:", error);
        this.isChatModuleActive = false;
      }
    },

    async initDarkMode() {
      // Default when there is no user preference: light mode.
      document.documentElement.removeAttribute("id");

      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      if (!token || !userId) return;

      try {
        const response = await wencesApi.get("/api/users/myprofile");
        const isDarkMode =
          response?.data?.ok && response?.data?.data?.darkMode === true;

        if (response?.data?.ok && response?.data?.data) {
          cacheUiZoom(userId, response.data.data.uiZoom);
          this.applyUiZoomForRoute(this.$route);
        }

        if (isDarkMode) {
          document.documentElement.id = "darkdesign";
        } else {
          document.documentElement.removeAttribute("id");
        }
      } catch (error) {
        console.error("Error loading dark mode preference:", error);
        document.documentElement.removeAttribute("id");
      }
    },

    /** Zoom de interfaz del usuario; en /templates/* siempre 100% (igual que dark.css). */
    applyUiZoomForRoute(route) {
      const isTemplateRoute = route?.path?.startsWith("/templates/");
      const hasSession =
        localStorage.getItem("token") && localStorage.getItem("userId");
      applyUiZoom(
        isTemplateRoute || !hasSession
          ? UI_ZOOM_DEFAULT
          : readCachedUiZoom(localStorage.getItem("userId"))
      );
    },

    watchAuthentication() {
      // Escuchar cambios en localStorage para detectar logout en otras pestañas
      window.addEventListener("storage", (e) => {
        if (e.key === "token" && !e.newValue) {
          // Token fue eliminado (logout)
          this.isChatModuleActive = false;
        } else if (
          e.key === "token" &&
          e.newValue &&
          !this.isChatModuleActive
        ) {
          // Token fue agregado (login)
          this.checkChatModuleStatus();
        }
      });
    },
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
}

/* En flujo normal: empuja el sitio hacia abajo, sin tapar toasts ni overlays */
.test-env-banner {
  flex-shrink: 0;
  width: 100%;
  min-height: var(--test-env-banner-h, 2.25rem);
  padding: 0.4rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  background: #ffc107;
  color: #664d03;
  border-bottom: 1px solid #e0a800;
  box-sizing: border-box;
}

.router-nav-loading-strip {
  position: fixed;
  top: calc(12px + var(--test-env-banner-h, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 20000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  font-size: 0.875rem;
  color: #333;
  pointer-events: none;
}

.router-nav-loading-strip-text {
  white-space: nowrap;
}
</style>

<style>
html#darkdesign .test-env-banner {
  background: #ffca2c;
  color: #3d2f00;
  border-bottom-color: #c9a000;
}

html#darkdesign .router-nav-loading-strip {
  background: #2b2b2b;
  color: #e8e8e8;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}
</style>
